<template>
  <BaseCard>
    <div class="tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:text-gray-800">
      <div class="tw:flex tw:justify-between tw:w-full tw:flex-col">
        <div>
          <p class="tw:text-xl tw:font-bold">{{ $t('form.info.title_invite_user') }}</p>
        </div>
        <div
          class="tw:flex tw:flex-col tw:md:flex-row tw:w-full tw:mt-4 tw:justify-between"
          :class="error ? 'tw:items-center' : 'tw:md:items-end'"
        >
          <div class="tw:flex">
            <BaseTextField
              :label="$t('form.labels.email_address')"
              :placeholder="$t('form.placeholder.email')"
              name="email"
              aria-label="email"
              type="email"
              class="tw:md:w-[300px] tw:w-full tw:mr-2 tw:mb-1 tw:md:mb-auto"
              :error="error ? $t(error) : false"
              :value="emailValue"
              @onInput="setEmail"
            />
            <BaseSelect
              id="'select-user-role'"
              :label="$t('form.labels.role')"
              class="tw:md:w-[300px] tw:w-full tw:md:mr-2 tw:mb-1 tw:md:mb-auto"
              :value="userRole"
              :options="availableUserRoles"
              display-text-key="displayText"
              @onChange="e => setUserRole(e)"
            />
          </div>
          <MainButton
            size="medium"
            class="tw:w-full tw:md:w-auto"
            :disabled="emailValue.length === 0"
            @click="submitInvitation"
          >
            <span v-if="isSaving"><SmallDotLoader color="#fff" /></span>
            <span v-else>{{ $t('form.actions.invite') }}</span>
          </MainButton>
        </div>
        <div class="tw:flex tw:items-center tw:mt-6">
          <InformationOutline class="tw:fill-none tw:stroke-gray-800 tw:mr-1 tw:mt-[2px]" />
          <p class="tw:text-sm">{{ $t('form.info.description.invite_user') }}</p>
        </div>
      </div>
    </div>
  </BaseCard>
</template>
<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import InformationOutline from '@/components/icons/InformationOutline.svg?component'
import BaseCard from '@/components/dashboard/BaseCard.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import BaseTextField from '@/components/base/inputs/BaseTextField.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import BaseSelect from '@/components/BaseSelect.vue'

import { validate, required, emailAddress } from '@/utils/propertyDetailsHelpers.ts'
import {
  useInviteUserMutation,
  useOrganisationUsers,
  useUsersInvitations,
} from '@/components/settings/teamSettings/queries'
import useToastNotifications from '@/components/notifications/useToastNotifications'
import { useSessionValues } from '@/composables/useSessionValues'

const { t } = useI18n()

const { inviteUser } = useInviteUserMutation()
const { language } = useSessionValues()
const { userOrganisationData } = useOrganisationUsers()
const { usersInvitations } = useUsersInvitations()

const error = ref(false)
const emailValue = ref('')
const userRole = ref('normal')

const availableUserRoles = computed(() => [
  {
    displayText: t('form.labels.user_role.admin'),
    value: 'admin',
  },
  { displayText: t('form.labels.user_role.normal'), value: 'normal' },
])
const isSaving = ref(false)

const setUserRole = role => {
  userRole.value = role
}

const setEmail = inputValue => {
  const validationError = validate(inputValue, [required, emailAddress])
  error.value = validationError !== undefined ? validationError : false
  emailValue.value = inputValue
}

const isEmailAdded = () => {
  const searchEmail = emailValue.value?.toLowerCase().trim()
  if (!searchEmail) return false

  const existsInUsers = userOrganisationData.value.some(item => item.email?.toLowerCase().trim() === searchEmail)
  const existsInInvitations = usersInvitations.value.some(
    item => item.invitedEmail?.toLowerCase().trim() === searchEmail
  )

  return existsInUsers || existsInInvitations
}

const { displayNotification } = useToastNotifications()
const submitInvitation = () => {
  const isEmail = isEmailAdded()
  if (isEmail) {
    emailValue.value = ''
    displayNotification({
      message: 'invite_user_error_notification',
      isTranslationKey: true,
      isHtml: false,
      type: 'error',
    })
    return
  }
  if (error.value) return
  if (emailValue.value === '') return

  // enables loader on the button
  isSaving.value = true

  inviteUser(
    {
      data: { invitedEmail: emailValue.value, language: language.value, role: userRole.value },
    },
    {
      onSuccess: () => {
        displayNotification({
          message: 'form.messages.success.invitation',
          isTranslationKey: true,
          isHtml: false,
          type: 'success',
        })
        isSaving.value = false
        emailValue.value = ''
      },
      onError: () => {
        isSaving.value = false
        displayNotification({
          message: 'form.messages.error.unknown',
          isTranslationKey: true,
          isHtml: false,
          type: 'error',
        })
      },
    }
  )
}
</script>
